/**-- external -- */
import React, { useRef, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import update from "immutability-helper";
import _ from "lodash";

/** -- internal -- */
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import { getDisplayName } from "Utils/HOCHelpers";
import { ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION } from "Constants/permissionConstants";
import ACLStore from "lib/aclStore";

/**-- relative -- */
import { getPermissionsQuery } from "../modules/Queries";
import {
  getContextPayload,
  mergeVariablesWithDefaults,
  updateContextsAndContextIdMap,
  areContextsValid,
} from "../modules/Utils";
import withRouteBasedControlParametersContext from "./withRouteBasedControlParametersContext";
import ACLStorage from "../aclStorageWrapper";

const DEFAULT_CONTROL_PARAMETER_TYPES = ["MODULE_SETTING", "PERMISSION"];
const DEFAULT_PORTAL_TYPE = "PLANNER";

const withControlParameters = config => Component => {
  const skipFunction = props => {
    return (
      (config.skip ? config.skip(props) : false) ||
      !ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
    );
  };

  const mapStateToProps = (_, ownProps) => {
    const { controlParameterContext } = ownProps;

    const shouldSkipQuery = skipFunction(ownProps);

    const defaultContextPayload = shouldSkipQuery
      ? []
      : getContextPayload(controlParameterContext);

    return {
      defaultContextPayload,
      shouldSkipQuery,
    };
  };
  function ControlParametersHOC(props) {
    const {
      contextId,
      shouldSkipContextSetup,
      isControlParameterQueryLoadingWithNoData,
      shouldSkipQuery,
      ...rest
    } = props;

    /**
     * HACK: Below logic should be part of a useEffect as it has nothing to do with rendering.
     * However, we want to make sure that before children components start reading permissions,
     * context is setup properly. So, we are setting contextId outside of the effect.
     *
     * The operations which are performed as part of side effect are not expensive, so they won't
     * create any performance issues.
     */
    const previousContextIdRef = useRef(null);

    /**
     * In cases where queries fetch control parameters across multiple contexts
     * or from outside the current context scope (e.g., Global Search, Rocket Ship),
     * we don't need to set up a specific context. This is because either:
     *   i) multiple contexts are involved, or
     *   ii) the query operates entirely outside the expected context boundaries.
     *
     * Also if query is skipped, we don't need to set up context.
     */

    if (!shouldSkipContextSetup && contextId && !shouldSkipQuery) {
      /**
       * Here, we are storing the position of contextId inside aclStorage. We need to do it
       * to make sure that we are updating correct contextId when context is updated.
       * (Ex: Moving from one class to another class)
       */
      if (previousContextIdRef.current !== contextId) {
        ACLStorage.removeContextById(previousContextIdRef.current);
        ACLStorage.appendContextId(contextId);
      }

      previousContextIdRef.current = contextId;
    }

    useEffect(() => {
      return () => {
        if (!shouldSkipContextSetup && !shouldSkipQuery) {
          ACLStorage.removeContextById(previousContextIdRef.current);
        }
      };
    }, [shouldSkipContextSetup, shouldSkipQuery]);

    const { getControlParameterBasedProps = _.noop } = config;

    const controlParameterRelatedProps = useMemo(() => {
      return {
        isControlParameterQueryLoadingWithNoData,
      };
    }, [isControlParameterQueryLoadingWithNoData]);

    const controlParameterBasedProps = getControlParameterBasedProps({
      controlParameterRelatedProps,
      ownProps: props,
    });

    return (
      <Component
        {...rest}
        {...(shouldSkipQuery ? {} : controlParameterBasedProps)}
      />
    );
  }

  const Wrapper = compose(
    withRouteBasedControlParametersContext,
    connect(mapStateToProps),
    graphql(getPermissionsQuery, {
      name: "getPermissionsQuery",
      skip: ({ shouldSkipQuery }) => shouldSkipQuery,
      options: props => {
        const { options } = config;

        const payload = options(props);

        const updatedPayload = update(payload, {
          fetchPolicy: fetchPolicy => fetchPolicy || "cache-and-network",
          variables: variables =>
            mergeVariablesWithDefaults({
              variables,
              defaultContextPayload: props.defaultContextPayload,
              defaultControlParamterTypes: DEFAULT_CONTROL_PARAMETER_TYPES,
              defaultPortalType: DEFAULT_PORTAL_TYPE,
            }),
        });

        return updatedPayload;
      },
      props: ({
        getPermissionsQuery,
        ownProps: {
          isData = true,
          isLoading = false,
          defaultContextPayload,
          shouldSkipQuery,
        },
      }) => {
        /**
         * If query is skipped, return minimal props to avoid unnecessary processing
         */
        if (shouldSkipQuery) {
          return {
            shouldSkipQuery,
          };
        }

        const { skipContextSetup = false } = config;

        const contextsPassedFromComponent = _.get(
          getPermissionsQuery,
          "variables.filters.contexts",
          []
        );

        const areMultipleContexts = _.size(contextsPassedFromComponent) > 1;

        const isContextPayloadValid = areContextsValid({
          contextsPassedFromComponent,
          defaultContextPayload,
        });

        /**
         * Skip context setup if:
         * 1. Config explicitly sets skipContextSetup to true
         * 2. Multiple contexts are being queried
         * 3. Component specific contexts don't match with default context payload
         */

        const shouldSkipContextSetup =
          skipContextSetup || areMultipleContexts || !isContextPayloadValid;

        const isPermissionsDataAvailable = !_.isEmpty(
          _.get(getPermissionsQuery, "platform", {})
        );

        /**
         * We need to set mappings particularly for multiple contexts scenario.(Ex: Admin Portal, Global Search)
         * However, to keeps things simple we are doing it for all the cases.
         */

        if (isPermissionsDataAvailable) {
          const data = _.get(
            getPermissionsQuery,
            "platform.aclControlParameters",
            []
          );
          const contexts = _.get(
            getPermissionsQuery,
            "variables.filters.contexts",
            []
          );

          updateContextsAndContextIdMap({
            queryResponse: data,
            contexts,
          });
        }

        return {
          shouldSkipQuery,
          isData: isPermissionsDataAvailable && isData,
          isLoading:
            _.includes(
              GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
              getPermissionsQuery.networkStatus
            ) || isLoading,
          contextId: _.get(
            getPermissionsQuery,
            "platform.aclControlParameters[0].uid",
            ""
          ),
          shouldSkipContextSetup,
          //TODO: Update property name to isControlParameterQueryLoadingWithOldData
          isPermissionsQueryInProgressWithOldData:
            _.includes(
              GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
              getPermissionsQuery.networkStatus
            ) && isPermissionsDataAvailable,
          isControlParameterQueryLoadingWithNoData:
            _.includes(
              GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
              getPermissionsQuery.networkStatus
            ) && !isPermissionsDataAvailable,
        };
      },
    })
  )(ControlParametersHOC);

  Wrapper.displayName = `withControlParameters(${getDisplayName(Component)})`;

  return Wrapper;
};

export default withControlParameters;
